/* eslint-disable max-classes-per-file */
import { TicketType } from "@/libs/server/goApiServer/experience/ExperienceServiceInterface/getEventTicketAvailableInterfaces";
import { ProductTypeEnums } from "@/types/product/account/history";

import { ResponsePresenter } from "../../common/commonClassPresenter";
import { CorporateDisCountPresenter } from "../../corporate/corporateServiceInterfaces/corporateInterface";
import {
  EventGuestPresenter,
  EventLocationPresenter,
} from "./getHistoryEventListInterface";
import { ExperienceDetailPaymentPrice } from "./getHistoryExperienceDetailInterface";
import {
  ContactDatePresenter,
  DayPlanDataPresenter,
  ExpirePaymentDatePresenter,
  PaymentChannelDataPresenter,
  PaymentCouponDiscountPresenter,
  RefundedPaymentDatePresenter,
} from "./historyBookingCommonInterface";

class EventDetailList {
  sku: string;

  type: string;

  name: string;

  amount: number;

  price: number;

  currency: string;

  shippingCosts: number;

  constructor(response: any) {
    this.sku = response?.sku || "";
    this.type = response?.type || "";
    this.name = response?.name || "";
    this.amount = response?.amount || 0;
    this.price = response?.price || 0;
    this.currency = response?.currency || "";
    this.shippingCosts = response?.shippingCosts || 0;
  }
}

class EventDetailPaymentPresenter {
  bookingDate: string;

  paymentSuccessDate: string;

  couponDiscount: PaymentCouponDiscountPresenter;

  paymentType: string;

  paymentUrl: string;

  isAllocatesExpire: boolean;

  isPaymentExpire: boolean;

  expirePaymentDate: ExpirePaymentDatePresenter;

  expireAllocateDate: ExpirePaymentDatePresenter;

  channel: PaymentChannelDataPresenter;

  refunded: RefundedPaymentDatePresenter;

  price: ExperienceDetailPaymentPrice;

  constructor(response: any) {
    this.paymentType = response?.paymentType || "";
    this.paymentUrl = response?.paymentUrl || "";
    this.isPaymentExpire = response?.isPaymentExpire || false;
    this.isAllocatesExpire = response?.isAllocatesExpire || false;
    this.expireAllocateDate = new ExpirePaymentDatePresenter(
      response?.expireAllocateDate,
    );
    this.expirePaymentDate = new ExpirePaymentDatePresenter(
      response?.expirePaymentDate,
    );
    this.channel = new PaymentChannelDataPresenter(response?.channel);
    this.couponDiscount = new PaymentCouponDiscountPresenter(
      response?.couponDiscount,
    );
    this.bookingDate = response?.bookingDate || "";
    this.paymentSuccessDate = response?.paymentSuccessDate || "";
    this.refunded = new RefundedPaymentDatePresenter(response?.refunded);
    this.price = new ExperienceDetailPaymentPrice(response?.price);
  }
}

class EventReviewRatingPresenter {
  numReviews: number;

  rating: number;

  textRatingDetail: string;

  constructor(res: any) {
    this.numReviews = res?.numReviews || 0;
    this.rating = res?.rating || 0;
    this.textRatingDetail = res?.textRatingDetail || "";
  }
}
class EventCategoryPresenter {
  name: string;

  slug: string;

  constructor(response: any) {
    this.name = response?.name || "";
    this.slug = response?.slug || "";
  }
}

class EventDetailInfoPresenter {
  name: string;

  seatName: string;

  banner: string;

  image: string;

  isGreenCertificate: boolean;

  reviewRating: EventReviewRatingPresenter;

  category: EventCategoryPresenter;

  subCategory: EventCategoryPresenter;

  team: EventCategoryPresenter;

  teamMaths: EventCategoryPresenter;

  location: EventLocationPresenter;

  seatingDescription: string;

  constructor(response: any) {
    this.name = response?.name || "";
    this.seatName = response?.seatName || "-";
    this.banner = response?.banner || "";
    this.image = response?.image || "";
    this.isGreenCertificate = response?.isGreenCertificate || false;
    this.reviewRating = new EventReviewRatingPresenter(
      response?.reviewRating || {},
    );
    this.category = new EventCategoryPresenter(response?.category);
    this.subCategory = new EventCategoryPresenter(response?.subCategory);
    this.team = new EventCategoryPresenter(response?.team);
    this.teamMaths = new EventCategoryPresenter(response?.teamMaths);
    this.location = new EventLocationPresenter(response?.location);
    this.seatingDescription = response?.seatingDescription || "";
  }
}

class EventDetailDataPresenter extends CorporateDisCountPresenter {
  bookForOther: string;

  bookingInternalNo: string;

  pinCode: string;

  reservationNo: string;

  price: number;

  currency: string;

  joinDateOri: string;

  joinDate: string;

  joinTime: string;

  round: string;

  specialOptions: string;

  seatName: string;

  numberOfCard: number;

  bookingStep: string;

  paymentStatus: string;

  bookingStatus: string;

  resendVoucher: boolean;

  resendPayment: boolean;

  dayPlan: DayPlanDataPresenter;

  contact: ContactDatePresenter;

  payment: EventDetailPaymentPresenter;

  guest: EventGuestPresenter[];

  list: EventDetailList[];

  info: EventDetailInfoPresenter;

  productType: string;

  ticketType: string;

  type: string;

  constructor(response: any) {
    super(response);
    this.bookForOther = response?.bookForOther || "";
    this.bookingInternalNo = response?.bookingInternalNo || "";
    this.pinCode = response?.pinCode || "";
    this.reservationNo = response?.reservationNo || "";
    this.price = response?.price || 0;
    this.currency = response?.currency || "";
    this.joinDateOri = response?.joinDateOri || "";
    this.joinDate = response?.joinDate || "";
    this.joinTime = response?.joinTime || "";
    this.round = response?.round || "-";
    this.specialOptions = response?.specialOptions || "-";
    this.seatName = response?.seatName || "-";
    this.numberOfCard = response?.numberOfCard || 0;
    this.bookingStep = (response?.bookingStep || "").toLocaleLowerCase();
    this.paymentStatus = (response?.paymentStatus || "").toLocaleLowerCase();
    this.bookingStatus = (response?.bookingStatus || "").toLocaleLowerCase();
    this.resendVoucher = response?.resendVoucher || false;
    this.resendPayment = response?.resendPayment || false;
    this.dayPlan = new DayPlanDataPresenter(response?.dayPlan);
    this.contact = new ContactDatePresenter(response?.contact);
    this.payment = new EventDetailPaymentPresenter(response?.payment);
    this.guest = (response?.guest || []).map(
      (results: any) => new EventGuestPresenter(results),
    );
    this.list = (response?.list || []).map(
      (results: any) => new EventDetailList(results),
    );
    this.info = new EventDetailInfoPresenter(response?.info);
    this.productType = response?.productType || ProductTypeEnums.EXPERIENCE;
    this.ticketType = response?.ticketType || TicketType.DAY_TRIP;
    this.type = response?.type || ProductTypeEnums.EXPERIENCE;
  }
}

export class ResponseGetDetailEventPresenter extends ResponsePresenter<EventDetailDataPresenter> {
  cToken: string;

  constructor(response: any) {
    super(response, (data) => new EventDetailDataPresenter(data));
    this.cToken = response?.cToken || "";
  }
}
